import { Component, HostListener } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DriverRecordRequestService } from '../../../services/driverrecordrequest.service';
import { GeneralService } from '../../../services/general.service';
import { IWizard } from '../../../interfaces/iwizard';
import { WizardSteps } from '../../../interfaces/wizard-steps';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { AuthenticationService } from '../../../services/authentication.service';
import { UserProfile } from '../../../interfaces/UserProfile';
import RenderDriverReportRequest from '../../../interfaces/renderReportRequest';
import { DriverReportView } from '../../../interfaces/driverReportView';
import { DriverService } from '../../../services/driver.service';
import { DateTime } from 'luxon';
import { TranslatorService } from '../../../services/translator.service';

@Component({
    selector: 'app-order-success',
    templateUrl: './order-success.component.html',
    styleUrls: ['./order-success.component.scss'],
    standalone: false
})
export class OrderSuccessComponent {
     public wizard!: IWizard;

     ccpToken: string = '';
     isLoading: boolean = true;
     userProfile?: UserProfile;
     username?: string;
     authMethod: string = 'Anonymous';
     accessedVia: string = 'DriverWizard';
     accessedBy: string = '';
     routeParams: ParamMap = {} as ParamMap;
     _step = 11;

     constructor(public _router: Router,
          public service: DriverRecordRequestService,
          public generalService: GeneralService,
          public authenticationService: AuthenticationService,
          public driverService: DriverService,
          private route: ActivatedRoute,
          private translatorService: TranslatorService
     ) { 
          this.wizard = this.service.getWizard();
     }

     ngOnInit(): void {
          window.history.pushState(null, "", window.location.href);
          this.isLoading = true;
          setTimeout(() => this.scrollToTop(), 500);
          this.translatorService.restoreBackupLanguage();
          this.routeParams = this.route.snapshot.queryParamMap;
          this.username = this.authenticationService.getUserName();
          this.userProfile = this.authenticationService.getUserProfile();

          let reload = false;
          if (this.routeParams && this.routeParams.has('token') && this.routeParams.get('token')?.toString()) {
               this.ccpToken = this.routeParams.get('token')?.toString() ?? '';
          }

          const navigationEntries = window.performance.getEntriesByType("navigation");
          if (window.performance && window.performance.navigation.type === 1) {
               // if reload we will not just reload the stored auth.
               reload = true;
          } else {
               this.driverService.clearAuthCode();
          }

          if (this.ccpToken) {
               this.accessedBy = this.service.driverRequest.emailAddress ?? '';
               if (this.username) {
                    this.authMethod = 'Login.gov';
                    this.accessedVia = 'DriverDashboard';
                    this.accessedBy = this.username;
               }

               if (!this.service.testCCPHistory(this.ccpToken) && reload === false) {
                    this.driverService.getAuthCode(this.ccpToken)
                         .then((authCode: string | null) => {
                              const action = this.service.isReorder() ? 'driver_request_record::reorder' : 'driver_request_record::new';
                              this.generalService.logActivity(action, this.username, [{name: 'auth_code', value: authCode ?? ''}],  [{name: 'ccp_token', value: this.ccpToken}]);

                              if (authCode) {
                                   this.isLoading = false;
                              } else {
                                   this._router.navigateByUrl('/customerSupportInfo');
                              }
                         }).catch((error: any) => {
                              this._router.navigateByUrl('/customerSupportInfo');
                         });
               } else if (this.driverService.getStoredAuthCode() !== '') {
                    this.driverService.state.authCode = this.driverService.getStoredAuthCode();
                    this.isLoading = false;
                    const action = this.service.isReorder() ? 'driver_request_record::reorder' : 'driver_request_record::new';
                    this.generalService.logActivity(action, this.username, null,  [{name: 'ccp_token', value: this.ccpToken}]);

               } else {
                    this.finish();
               }
          } else {
               this._router.navigateByUrl('/customerSupportInfo');
          }

          if (this.service.isReorder()) {
               this._step = 7;
          }
     }

     showInBrowser() {
          if (this.driverService.state.authCode && this.driverService.state.authCode.length > 0) {
               const request: RenderDriverReportRequest = { asPdf: false, authCode: this.driverService.state.authCode, driverIP: '' };
               this.generalService.logActivity('driver_record::view',this.service.driverRequest!.emailAddress!, 
               [{name: 'auth_code', value: this.driverService.state.authCode}], 
               [{name: 'info', value: 'View HTML report from Order Success page.'}]);        
               this.service.getEndUserIp()
                    .then((result: any) => {
                         request.driverIP = result;
                         const reportView: DriverReportView = {
                              accessedVia: this.accessedVia,
                              accessedBy: this.accessedBy,
                              accessorCustomerId: '2147483647',
                              authCode: this.driverService.state.authCode,
                              authenticationMethod: this.authMethod,
                              accessedDateTime: DateTime.now().toJSDate()
                         }
                         this.service.openDriverReport(request, reportView)
                              .then((v: any) => {
                                   this.openInBrowser(v);
                              })
                              .catch((error: any) => {
                              });
                    }).catch((error: any) => {
                         console.error(error);
                         const reportView: DriverReportView = {
                              accessedVia: this.accessedVia,
                              accessedBy: this.accessedBy,
                              accessorCustomerId: '2147483647',
                              authCode: this.driverService.state.authCode,
                              authenticationMethod: this.authMethod,
                              accessedDateTime: DateTime.now().toJSDate()
                         }
                         this.service.openDriverReport(request, reportView)
                              .then((v: any) => {
                                   this.openInBrowser(v);
                              })
                              .catch((error: any) => {
                              });
                    });
          } else {
               this._router.navigateByUrl('/');
          }
     }

     openInBrowser(contentToRender: string) {
          if (contentToRender && contentToRender.length === 0) {
               this._router.navigateByUrl('/customerSupportInfo');
          }
          const blob = new Blob([contentToRender], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          url.split("/");
          const faxWindow = window.open(url);
          if (faxWindow) {
               faxWindow.onload = () => {
                    URL.revokeObjectURL(url);
               };
               faxWindow?.focus();
          } else {
               // If popup blocked
               console.warn('Popup blocked. Please allow popups for this site.');
               URL.revokeObjectURL(url); // Revoke even if not opened to prevent memory leaks
          }
     }

     downloadPdf() {
          this.generalService.logActivity('driver_record::view',this.service.driverRequest!.emailAddress!, 
               [{name: 'auth_code', value: this.driverService.state.authCode}], 
               [{name: 'info', value: 'PDF downloaded from Order Success page.'}]);    
          const reportView: DriverReportView = {
               accessedVia: this.accessedVia,
               accessedBy: this.accessedBy,
               accessorCustomerId: '2147483647',
               authCode: this.driverService.state.authCode,
               authenticationMethod: this.authMethod,
               accessedDateTime: new Date()
          }
          this.service.getDriverReportPdf(this.driverService.state.authCode, reportView)
               .then((v: any) => this.openPdf(v))
               .catch((error: any) => {
               });
     }

     openPdf(buffer: any) {
          const filename = "My_PSP_Driver_Report.pdf";
          const element = document.createElement('a');
          element.setAttribute('href', 'data:application/octet-stream;base64,' + buffer);
          element.setAttribute('download', filename);
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
     }

     translate(translationTag: string): string {
          let translatedText: string = this.generalService.getTranslation(translationTag, 'customer')
          if (translatedText === undefined) {
               translatedText = this.generalService.getTranslation(translationTag, 'customer');
          }
          return translatedText;
     }

     get hasErrors(): boolean {
          return true;
     }

     finish() {
          if (this.username) {
               this.scrollToTop();
               this._router.navigateByUrl('/driverDashboard');
          } else {
               this.scrollToTop();
               this.driverService.clearAuthCode();
               this._router.navigateByUrl('/home');
          }
     }

     scrollToTop() {
          window.scrollTo(0, 0);
     }

     navigateToStateMVR() {
          this._router.navigate(['/MvrByState']);
     }
}
